/**
 * 사내결재 문서함 — [법인카드 사용내역] 탭 (v2.50.0 신설 · v2.55.0 메뉴 개명).
 *
 *   전 직원: 내게 지정된 카드 사용내역(월별) — [작성 시작] 하면 일자·사용처·금액이
 *     자동으로 채워진 법인카드사용내역서 초안이 만들어지고, 계정과목·내용·비고를
 *     채워 제출한다. 이미 작성한 건은 이어서 작성/보기.
 *
 *   경영지원(김소라·김초은 — app_config 지정 계정)·admin 만: 업로드 패널 + 전체 현황.
 *     카드사 홈페이지에서 받은 엑셀(승인일시·카드번호·이용가맹점명·승인금액)을
 *     올리고 대상자 1명을 지정한다.
 */

import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../../lib/expense-api';
import { ExpenseDocModal, EXPENSE_STATUS_BADGE } from '../../components/ExpenseDocModal';
import { useAuth } from '../../lib/auth-context';
import { LoadingLines } from '../../components/ui/Skeleton';

export function CardStatementTab() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [openDoc, setOpenDoc] = useState<{ id: number; edit: boolean } | null>(null);

  const listQuery = useQuery({
    queryKey: ['expense', 'card-statements'],
    queryFn: api.listCardStatements,
    staleTime: 15_000,
  });
  const rows = listQuery.data?.rows ?? [];
  const canUpload = listQuery.data?.canUpload ?? false;
  // 남의 명세서가 보이는 사람인가 (v2.134.0) — 경영지원·admin·대표이사·부서장.
  // 전에는 canUpload(=올릴 수 있는 사람)로 전체 현황 표를 가려서 대표이사가 빈 화면이었다.
  const seeOthers = listQuery.data?.seeOthers ?? canUpload;
  const mine = rows.filter((r) => r.targetUser === user?.userId);

  /**
   * 전체 현황 찾기 (v2.134.0) — 이제 **완결된 카드 문서를 여는 유일한 통로**라
   * 연도와 검색이 없으면 해가 갈수록 훑을 수 없다(카드 문서는 사람×12개월로 는다).
   * ⚠서버는 이미 300건 상한이 있다 — 거르기는 화면에서 한다(받아 온 것 안에서만).
   */
  const [cardYear, setCardYear] = useState<string>('');
  const [cardQ, setCardQ] = useState('');
  const cardYears = useMemo(
    () => [...new Set(rows.map((r) => r.yearMonth.slice(0, 4)))].sort().reverse(),
    [rows],
  );
  const visibleRows = useMemo(() => {
    const t = cardQ.trim();
    return rows.filter((r) => {
      if (cardYear && !r.yearMonth.startsWith(cardYear)) return false;
      if (!t) return true;
      return [r.targetName, r.targetUser, r.docNo, r.cardNo, r.origFilename, r.yearMonth]
        .some((v) => (v ?? '').includes(t));
    });
  }, [rows, cardYear, cardQ]);

  /**
   * 전체 업로드 현황을 **대상자별로 묶는다** (v2.62.0 · 2026-08-07 사용자 요청).
   * 접었을 때 = 이름 + 가장 최근 올린 파일. 펼치면 그 사람 것 전부.
   * ⚠서버가 이미 보여도 되는 행만 준다(경영지원·admin=전체 / 그 외=본인) — 여기선 묶기만 한다.
   */
  const groups = useMemo(() => {
    const byTarget = new Map<string, { targetUser: string; targetName: string; items: typeof rows }>();
    for (const r of visibleRows) {
      const g = byTarget.get(r.targetUser)
        ?? { targetUser: r.targetUser, targetName: r.targetName ?? r.targetUser, items: [] };
      g.items.push(r);
      byTarget.set(r.targetUser, g);
    }
    return [...byTarget.values()]
      .map((g) => {
        const items = [...g.items].sort((a, b) => b.yearMonth.localeCompare(a.yearMonth));
        return {
          ...g,
          items,
          latest: items[0]!,
          notWritten: items.filter((x) => !x.docId).length,
        };
      })
      // 가장 최근 자료(=귀속월이 늦은 것)를 가진 사람이 위로. 업로드 시각이 아니다.
      // (서버 목록도 원래 ORDER BY year_month DESC 라 같은 축이다 — card-statements.ts)
      .sort((a, b) => b.latest.yearMonth.localeCompare(a.latest.yearMonth)
        || a.targetName.localeCompare(b.targetName));
  }, [visibleRows]);

  /** 펼친 대상자 — 기본 전부 접힘. 키는 targetUser **문자열** */
  const [openTargets, setOpenTargets] = useState<Set<string>>(new Set());

  const users = useQuery({
    queryKey: ['expense', 'users'],
    queryFn: api.listPortalUsers,
    staleTime: 300_000,
    enabled: canUpload,
  });

  // 업로드 폼
  const [file, setFile] = useState<File | null>(null);
  const [targetUser, setTargetUser] = useState('');
  const [yearMonth, setYearMonth] = useState('');

  const uploadMut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('카드사 엑셀 파일을 선택하세요');
      if (!targetUser) throw new Error('작성할 대상자를 선택하세요');
      return api.uploadCardStatement({ file, targetUser, yearMonth: yearMonth || undefined });
    },
    onSuccess: (r) => {
      setError(null);
      setFile(null);
      setTargetUser('');
      setYearMonth('');
      qc.invalidateQueries({ queryKey: ['expense', 'card-statements'] });
      alert(`업로드 완료 — ${r.yearMonth} 사용내역 ${r.rowCount}건이 지정한 대상자에게 표시됩니다.`);
    },
    onError: (e: Error) => setError(e.message),
  });

  const createMut = useMutation({
    mutationFn: (statementId: number) =>
      api.createExpenseDoc({ docType: '법인카드사용내역서', statementId, items: [] }),
    onSuccess: (r) => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['expense'] });
      setOpenDoc({ id: r.id, edit: true });
    },
    onError: (e: Error) => setError(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (v: { id: number; withDocs?: boolean }) =>
      api.deleteCardStatement(v.id, v.withDocs ?? false),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['expense', 'card-statements'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  // 업로드 원본 다시 받기 (v2.56.0) — 파일이 보관된 건만 버튼이 보인다
  const downloadMut = useMutation({
    mutationFn: (r: { id: number; origFilename: string }) =>
      api.downloadCardStatementFile(r.id, r.origFilename),
    onSuccess: () => setError(null),
    onError: (e: Error) => setError(e.message),
  });

  const selCls = 'ui-input';

  /**
   * 표 제목 — 부서장에게는 「전체」가 아니라 자기 부서만 보인다(v2.134.0 리뷰 지적).
   * 제목이 사실과 다르면 「우리 부서 사람이 빠졌다」는 문의가 된다.
   */
  const seeAllTitle = canUpload || listQuery.data?.rows.some((r) => r.targetUser !== user?.userId)
    ? (canUpload ? '전체 업로드 현황' : '업로드 현황 (내가 볼 수 있는 범위)')
    : '업로드 현황';

  return (
    <div className="space-y-4">
      {error && (
        <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">{error}</div>
      )}

      {/* 경영지원·admin — 업로드 패널 */}
      {canUpload && (
        <div className="bg-white rounded shadow-sm border border-slate-200 p-5">
          <h2 className="ui-section-title">법인카드 사용내역 업로드 <span className="text-xs font-normal text-slate-500">(경영지원부·admin 전용)</span></h2>
          <p className="text-xs text-slate-500 mt-0.5 mb-3">
            카드사 홈페이지에서 받은 법인카드별 사용내역 엑셀(승인일시·카드번호·이용가맹점명·승인금액)을
            올리고 작성할 직원 1명을 지정하세요. 지정된 직원의 이 탭에 작성 목록이 나타납니다.
          </p>
          <div className="flex items-end gap-2 flex-wrap">
            <label className="ui-label">
              엑셀 파일
              <input type="file" accept=".xlsx"
                className="block text-sm mt-1"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </label>
            <label className="ui-label">
              대상자 (1명)
              <select className={`${selCls} block mt-1 min-w-[180px]`} value={targetUser}
                onChange={(e) => setTargetUser(e.target.value)}>
                <option value="">선택하세요</option>
                {(users.data ?? []).map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.name}{u.department ? ` (${u.department}${u.title ? ` ${u.title}` : ''})` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="ui-label">
              귀속월 (비우면 파일에서 자동)
              <input type="month" className={`${selCls} block mt-1`} value={yearMonth}
                onChange={(e) => setYearMonth(e.target.value)} />
            </label>
            <button type="button" disabled={uploadMut.isPending}
              onClick={() => uploadMut.mutate()}
              className="btn btn-primary">
              {uploadMut.isPending ? '업로드 중…' : '업로드'}
            </button>
          </div>
        </div>
      )}

      {/* 내게 지정된 사용내역 */}
      <div className="bg-white rounded shadow-sm border border-slate-200 p-5">
        <h2 className="ui-section-title">내 법인카드 사용내역 작성</h2>
        <p className="text-xs text-slate-500 mt-0.5 mb-3">
          경영지원부가 지정해 준 월별 사용내역 — [작성 시작]을 누르면 일자·사용처·금액이 자동으로
          채워지고, 계정과목·내용·비고만 채워 제출하면 됩니다.
        </p>
        {/* 불러오는 중·실패를 「없습니다」와 가른다 (v2.232.0 · 점검 ③ H-8) — 전에는 목록이 실패해도 「지정된 사용내역이 없습니다」로 보였고,
            올리기 상자(경영지원)도 목록 응답의 canUpload 를 못 받아 함께 사라졌다 */}
        {/* ⚠isLoading 이 아니라 isPending — 창이 가려져 있으면 다시 시도가 멈춰(paused) isLoading 이 false 가 되는데,
            그때도 아직 자료가 없으니 「없습니다」가 아니라 「불러오는 중」이다(확인용 창에서 실측) */}
        {listQuery.isPending ? (
          <div className="text-sm text-slate-400 py-4 text-center"><LoadingLines /></div>
        ) : listQuery.isError && !listQuery.data ? (
          <div className="text-sm text-red-600 py-4 text-center">
            사용내역 목록을 불러오지 못했습니다 — {(listQuery.error as Error)?.message || '잠시 뒤 다시 시도해 주세요'}
            <div className="mt-2">
              <button
                type="button"
                onClick={() => { void listQuery.refetch(); }}
                className="btn btn-danger btn-sm"
              >
                다시 불러오기
              </button>
            </div>
            <div className="mt-1 text-xs text-slate-500">(경영지원부의 사용내역 올리기 상자도 목록을 불러온 뒤에 보입니다)</div>
          </div>
        ) : mine.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center">지정된 사용내역이 없습니다</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-xs text-slate-500">
                <th className="py-2 pr-2">귀속월</th>
                <th className="py-2 pr-2">카드</th>
                <th className="py-2 pr-2 text-right">건수</th>
                <th className="py-2 pr-2">올린 사람</th>
                <th className="py-2 pr-2">업로드일</th>
                <th className="py-2 pr-2">원본 파일</th>
                <th className="py-2 pr-2">상태</th>
                <th className="py-2 w-[120px]"></th>
              </tr>
            </thead>
            <tbody>
              {mine.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="py-2 pr-2 font-medium whitespace-nowrap">
                    {r.yearMonth}
                    <BackfillBadge note={r.backfillNote} />
                  </td>
                  <td className="py-2 pr-2 text-slate-500">{r.cardNo ?? '-'}</td>
                  <td className="py-2 pr-2 text-right num">{r.rowCount}</td>
                  <td className="py-2 pr-2 text-slate-600">{r.uploaderName}</td>
                  <td className="py-2 pr-2 text-slate-500">{r.uploadedAt.slice(0, 10)}</td>
                  <td className="py-2 pr-2">
                    <FileCell row={r} onDownload={() => downloadMut.mutate(r)} busy={downloadMut.isPending} />
                  </td>
                  <td className="py-2 pr-2">
                    {r.docStatus ? (
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${EXPENSE_STATUS_BADGE[r.docStatus] ?? ''}`}>
                        {r.docStatus}
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-500 font-semibold">미작성</span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    {r.docId ? (
                      <button type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setOpenDoc({ id: r.docId!, edit: false })}>
                        {r.docStatus === '작성중' || r.docStatus === '반려' ? '이어서 작성' : '보기'}
                      </button>
                    ) : (
                      <button type="button" disabled={createMut.isPending}
                        className="btn btn-primary btn-sm"
                        onClick={() => createMut.mutate(r.id)}>
                        작성 시작
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 전체 현황 (v2.62.0: 대상자별로 접고 펼친다 · v2.134.0: 결재자도 본다) */}
      {seeOthers && (
        <div className="bg-white rounded shadow-sm border border-slate-200 p-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
            <div>
              <h2 className="ui-section-title">{seeAllTitle}</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                <b>대상자 줄을 클릭하면</b> 그 사람에게 올린 월별 내역이 펼쳐집니다.
              </p>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {/* 연도·검색 (v2.134.0) — 완결 카드 문서를 여는 유일한 통로라 훑을 수단이 있어야 한다 */}
              <select value={cardYear} onChange={(e) => setCardYear(e.target.value)}
                className="ui-input ui-input-sm">
                <option value="">전체 연도</option>
                {cardYears.map((y) => <option key={y} value={y}>{y}년</option>)}
              </select>
              <input value={cardQ} onChange={(e) => setCardQ(e.target.value)}
                placeholder="이름·문서번호·카드·파일 검색"
                className="ui-input ui-input-sm w-48" />
            </div>
            {groups.length > 0 && (
              <div className="flex gap-1.5">
                <button type="button"
                  onClick={() => setOpenTargets(new Set(groups.map((g) => g.targetUser)))}
                  className="btn btn-secondary btn-sm">
                  전체 펼치기
                </button>
                <button type="button" onClick={() => setOpenTargets(new Set())}
                  className="btn btn-secondary btn-sm">
                  전체 접기
                </button>
              </div>
            )}
          </div>
          <div className="text-xs text-slate-500 mb-2 mt-2">
            대상자 <b className="num">{groups.length}</b>명 · 업로드 <b className="num">{rows.length}</b>건
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-xs text-slate-500">
                  <th className="py-2 pr-2 min-w-[130px]">몇 월분</th>
                  <th className="py-2 pr-2 min-w-[150px]">카드</th>
                  <th className="py-2 pr-2 text-right min-w-[60px]">건수</th>
                  <th className="py-2 pr-2 min-w-[220px]">원본 파일</th>
                  <th className="py-2 pr-2 min-w-[90px]">올린 사람</th>
                  <th className="py-2 pr-2 min-w-[100px]">업로드일</th>
                  <th className="py-2 pr-2 min-w-[90px]">작성 상태</th>
                  <th className="py-2 w-[60px]"></th>
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 ? (
                  <tr><td colSpan={8} className="py-6 text-center text-slate-400">업로드 내역이 없습니다</td></tr>
                ) : groups.map((g) => {
                  const open = openTargets.has(g.targetUser);
                  return (
                    <Fragment key={g.targetUser}>
                      {/* 대상자 줄 — 이름 · 최근 한 건 · 총계 */}
                      <tr className="border-t border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer"
                        onClick={() => setOpenTargets((prev) => {
                          const next = new Set(prev);
                          if (next.has(g.targetUser)) next.delete(g.targetUser); else next.add(g.targetUser);
                          return next;
                        })}>
                        <td colSpan={8} className="px-4 py-3">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-slate-400 w-3 shrink-0">{open ? '▼' : '▶'}</span>
                            <span className="font-bold text-slate-800">{g.targetName}</span>
                            <span className="text-xs text-slate-500">
                              최근{' '}
                              <b className="text-slate-600 font-medium">{ymLabel(g.latest.yearMonth)}</b>
                              <span className="ml-1.5 text-slate-500">{g.latest.origFilename}</span>
                              <BackfillBadge note={g.latest.backfillNote} />
                            </span>
                            <span className="ml-auto text-xs text-slate-500">
                              모두 <b className="num text-slate-700">{g.items.length}</b>건
                              {g.notWritten > 0 && (
                                <span className="ml-2 text-amber-700">미작성 {g.notWritten}건</span>
                              )}
                            </span>
                          </div>
                        </td>
                      </tr>

                      {open && g.items.map((r) => (
                        <tr key={r.id} className="border-t border-slate-100">
                          <td className="py-2 pr-2 whitespace-nowrap">
                            <span className="text-slate-300 mr-1.5">└</span>
                            {ymLabel(r.yearMonth)}
                            <BackfillBadge note={r.backfillNote} />
                          </td>
                          <td className="py-2 pr-2 text-slate-500">{r.cardNo ?? '-'}</td>
                          <td className="py-2 pr-2 text-right num">{r.rowCount}</td>
                          <td className="py-2 pr-2 max-w-[260px]">
                            <FileCell row={r} onDownload={() => downloadMut.mutate(r)} busy={downloadMut.isPending} showName />
                          </td>
                          <td className="py-2 pr-2 text-slate-600">{r.uploaderName}</td>
                          <td className="py-2 pr-2 text-slate-500">{r.uploadedAt.slice(0, 10)}</td>
                          <td className="py-2 pr-2">
                            {r.docStatus ? (
                              <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${EXPENSE_STATUS_BADGE[r.docStatus] ?? ''}`}>
                                {r.docStatus}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-500">미작성</span>
                            )}
                          </td>
                          <td className="py-2 text-right whitespace-nowrap">
                            {/* 📄 보기 (v2.134.0) — 완결된 카드 문서를 여는 유일한 통로.
                                전에는 「최종승인」 배지만 찍고 누를 곳이 없었다. 열람 권한은 서버 상세 가드가 판정한다. */}
                            {/* ⚠「작성중」 문서는 **작성자만** 열 수 있다(서버 열람 규칙) —
                                단추를 붙이면 눌러도 404 가 나고 모달이 빈 채로 멈춘다(리뷰에서 5개 관점 지적). */}
                            {r.docId && r.docStatus !== '작성중' && (
                              <button type="button"
                                title={`결재문서 ${r.docNo ?? ''} 열기`}
                                className="btn btn-secondary btn-sm mr-1"
                                onClick={() => setOpenDoc({ id: r.docId!, edit: false })}>보기</button>
                            )}
                            {r.docId && r.docStatus === '작성중' && (
                              <span className="text-xs text-slate-500 mr-1"
                                title="작성 중인 문서는 작성자 본인만 열 수 있습니다">작성 중</span>
                            )}
                            {/* 삭제 = 올린 본인·admin (canDelete·canDeleteWithDoc 는 서버 판정).
                                문서가 딸린 건은 그 문서까지 함께 지울지 한 번 더 물어본다 (v2.109.0) */}
                            {r.canDelete || r.canDeleteWithDoc ? (
                              <button type="button"
                                title={r.canDeleteWithDoc
                                  ? `업로드 삭제 — 작성된 문서(${r.docNo})도 함께 지웁니다`
                                  : '업로드 삭제 (올린 본인·admin, 작성 전만)'}
                                className="text-slate-300 hover:text-rose-500"
                                onClick={() => {
                                  const head = `${r.targetName} / ${r.yearMonth} 업로드를 삭제할까요?`;
                                  const msg = r.canDeleteWithDoc
                                    ? `${head}\n\n⚠ 이 업로드로 만들어진 문서 ${r.docNo}(${r.docStatus})도 함께 삭제됩니다.\n(문서에 붙은 영수증 첨부도 같이 지워집니다)`
                                    : `${head}\n(올린 원본 파일도 함께 지워집니다)`;
                                  if (confirm(msg)) {
                                    deleteMut.mutate({ id: r.id, withDocs: r.canDeleteWithDoc });
                                  }
                                }}>×</button>
                            ) : (
                              <span className="text-xs text-slate-400"
                                title={r.docId ? '작성된 문서가 있어 삭제할 수 없습니다' : '올린 본인과 admin 만 삭제할 수 있습니다'}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {openDoc && (
        <ExpenseDocModal
          docId={openDoc.id}
          startInEdit={openDoc.edit}
          onClose={() => setOpenDoc(null)}
          onChanged={() => qc.invalidateQueries({ queryKey: ['expense', 'card-statements'] })}
        />
      )}
    </div>
  );
}

/** 'YYYY-MM' → 「2026년 7월분」 (v2.62.0 · 목록에서 「몇 월분」인지 바로 읽히게) */
function ymLabel(ym: string): string {
  const [y, m] = (ym ?? '').split('-');
  return y && m ? `${y}년 ${Number(m)}월분` : (ym ?? '-');
}

/**
 * 「소급」 배지 (v2.57.1) — 포털이 생기기 전에 엑셀·종이로 결재를 끝내고
 * 나중에 집어넣은 건. 마우스를 올리면 어느 파일에서 왔는지 나온다.
 */
function BackfillBadge({ note }: { note: string | null }) {
  if (!note) return null;
  return (
    <span title={note}
      className="ml-1 px-1 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-700 text-[11px] font-semibold align-middle">
      소급
    </span>
  );
}

/**
 * 원본 파일 칸 (v2.56.0) — 보관돼 있으면 [⬇ 받기], 아니면 왜 못 받는지 알린다.
 * v2.56.0 이전 업로드분은 원본을 저장하지 않아 hasFile=false 다. 없는 걸 있는 척하지 않는다.
 */
function FileCell({
  row, onDownload, busy, showName,
}: {
  row: api.CardStatementRow;
  onDownload: () => void;
  busy: boolean;
  showName?: boolean;
}) {
  if (!row.hasFile) {
    return (
      <span className="text-xs text-slate-500"
        title="이 건은 원본 파일 보관 기능이 생기기 전에 올라와 다시 받을 수 없습니다">
        보관 안 됨
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 min-w-0">
      <button type="button" disabled={busy} onClick={onDownload}
        title={`원본 엑셀 받기 — ${row.origFilename}`}
        className="btn btn-secondary btn-sm shrink-0">
        {busy ? '받는 중…' : '⬇ 받기'}
      </button>
      {showName && (
        <span className="text-xs text-slate-500 truncate" title={row.origFilename}>
          {row.origFilename}
        </span>
      )}
    </span>
  );
}
